/**
 * Mock implementation of Node.js crypto module for browser environment
 */

export interface HashObject {
  update: (data: string) => {
    digest: (encoding: string) => string;
  };
}

// Mock crypto module functions
export const randomBytes = (size: number) => {
  console.log(`[MockCrypto] randomBytes: ${size}`);
  
  // Generate a random string
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < size; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return {
    toString: (encoding: string): string => {
      if (encoding === 'hex') {
        // Convert to hex-like string (not real conversion)
        return Array.from(result).map(c => 
          c.charCodeAt(0).toString(16).padStart(2, '0')
        ).join('');
      }
      return result;
    }
  };
};

// RFC-4122-shaped v4 UUID. Not cryptographically random (browser mock), but
// respects installDeterministicGlobals() seeding since it goes through Math.random.
export const randomUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const createHash = (algorithm: string): HashObject => {
  console.log(`[MockCrypto] createHash: ${algorithm}`);
  
  return {
    update: (data: string) => {
      return {
        digest: (encoding: string): string => {
          // Not a real hash, just for mocking
          const chars = 'abcdef0123456789';
          let result = '';
          // Create deterministic "hash" based on input
          const seed = data.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
          for (let i = 0; i < 32; i++) {
            result += chars.charAt((seed + i) % chars.length);
          }
          return result;
        }
      };
    }
  };
};

// Cluster trust (CurveZMQ keys, join tokens) runs only on real Node servers. These
// exist so @gotti/server's browser bundle links; calling one in the browser is a bug.
const browserUnavailable = (name: string) => (): never => {
  throw new Error(`crypto.${name} is not available in the browser (cluster trust is server-only)`);
};
export const createPrivateKey = browserUnavailable('createPrivateKey');
export const createPublicKey = browserUnavailable('createPublicKey');
export const createHmac = browserUnavailable('createHmac');
export const timingSafeEqual = browserUnavailable('timingSafeEqual');

// Export all crypto functions
export default {
  randomBytes,
  randomUUID,
  createHash,
  createPrivateKey,
  createPublicKey,
  createHmac,
  timingSafeEqual
};
